import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  debounce
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { UserContext } from "../../../../Context/UserContext";
import { Center } from "../../../../Models/Center";
import {
  CenterMessage,
  CenterMessageCenter,
  EnhancedCenterMessage,
  EnhancedCenterMessageCenter
} from "../../../../Models/CenterMessage";
import { DynamicContentTab, DynamicContentView } from "../../../../Types/DynamicContentView";
import { Error } from "../../../../Utils/Errors";
import { useApi } from "../../../../Utils/useApi";
import DynamicContentPanel from "../../../DynamicContentPanel/DynamicContentPanel";
import AdminReadmeFormLabel from "../../AdminReadmeFormLabel";
import ToolWrapper from "../ToolWrapper";
import { CenterMessagesReadme } from "./CenterMessages.Readme";

const CenterMessages: React.FC = () => {
  const { dynamicContent } = useContext(UserContext);
  const [dynamicContentView, setDynamicContentView] = useState(
    () => dynamicContent && new DynamicContentView(dynamicContent)
  );

  const [previewDate, setPreviewDate] = useState<DateTime>(DateTime.now());
  const [timeRange, setTimeRange] = useState<{ start: DateTime; end: DateTime }>({
    start: DateTime.now().minus({ days: 30 }),
    end: DateTime.now().plus({ days: 30 })
  });
  const [timeRangeDraft, setTimeRangeDraft] = useState<{ start: DateTime | null; end: DateTime | null }>({
    start: DateTime.now().minus({ days: 30 }),
    end: DateTime.now().plus({ days: 30 })
  });
  const timeRangeDraftRef = useRef<{ start: DateTime | null; end: DateTime | null }>({
    start: DateTime.now().minus({ days: 30 }),
    end: DateTime.now().plus({ days: 30 })
  });
  const [centerMessages, setCenterMessages] = useState<EnhancedCenterMessageCenter[]>([]);
  const [selectedCmc, setSelectedCmc] = useState<EnhancedCenterMessageCenter>();
  const [selectedMessage, setSelectedMessage] = useState<EnhancedCenterMessage>();
  const [updateMessage, setUpdateMessage] = useState<EnhancedCenterMessage>();
  const [isEditing, setIsEditing] = useState(false);
  const [messageMarkdown, setMessageMarkdown] = useState("");
  const [messageTitle, setMessageTitle] = useState("");
  const timeSpanCommitTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!dynamicContent) return;
    setDynamicContentView(new DynamicContentView(dynamicContent));
  }, [dynamicContent]);

  // update dynamic content preview
  useEffect(() => {
    let previewMessages: EnhancedCenterMessage[] | undefined;
    if (selectedCmc) {
      previewMessages = selectedCmc.messages.map(m => ({ ...m }));
      if (selectedMessage?.id) {
        const index = selectedCmc.messages.findIndex(m => m.id === selectedMessage.id);
        if (index !== -1) previewMessages[index] = { ...selectedMessage };
        else previewMessages.unshift(selectedMessage);
      } else if (selectedMessage) previewMessages.unshift(selectedMessage);
    } else if (selectedMessage) previewMessages = [selectedMessage];
    const tabs = (dynamicContent || []).map(dc => new DynamicContentTab(dc));
    const centerMessages = previewMessages && EnhancedCenterMessage.filterMessages(previewMessages, previewDate);
    setDynamicContentView(oldView => {
      if (!oldView) return oldView;
      const centerTabExists = oldView.tabs[0].title.includes("Center");
      if (centerMessages?.length) {
        const newTab = centerTabExists
          ? {
              ...oldView.tabs[0],
              title: `Center (${centerMessages.length})`,
              content: EnhancedCenterMessage.buildCentersContent(centerMessages)
            }
          : EnhancedCenterMessage.buildCentersContentTab(centerMessages);
        if (selectedMessage?.id) newTab.showChildIndex = newTab.content.findIndex(c => c.id === selectedMessage.id);
        tabs.unshift(newTab);
      } else if (centerTabExists) oldView.tabs.splice(0, 1);
      return { ...oldView, tabs };
    });
  }, [selectedCmc, selectedMessage, dynamicContent, previewDate]);

  // #region GET Center Messages
  const { response: getMessages, isLoading: getMessagesLoading } = useApi<CenterMessageCenter[]>({
    method: "get",
    url: "Legacy/V0/centers/messages",
    config: {
      params: { startDate: timeRange.start.toISODate(), endDate: timeRange.end.toISODate() }
    },
    error: Error.GetCentersMessages
  });
  useEffect(() => {
    if (getMessages)
      setCenterMessages(
        getMessages.map(cm => ({
          ...cm,
          messages: cm.messages.map(m => new EnhancedCenterMessage(m))
        }))
      );
  }, [getMessages]);
  // #endregion

  // #region Update or Add Center Message
  const { response: upsertMessage, isLoading: upsertMessageLoading } = useApi<CenterMessage, CenterMessage>({
    method: updateMessage?.id ? "put" : "post",
    url: `Legacy/V0/Centers/Messages${updateMessage?.id ? `/${updateMessage.id}` : ""}`,
    data: updateMessage,
    valid: Boolean(updateMessage),
    error: updateMessage?.id ? Error.PutCenterMessage : Error.PostCenterMessage
  });
  useEffect(() => {
    if (upsertMessage) {
      const enhanced = new EnhancedCenterMessage(upsertMessage);
      setCenterMessages(oldMessages => {
        let isUpdate = false;
        const newMessages = oldMessages.map(message => {
          if (!enhanced.centers.some(c => c.id === message.center.id)) return message;
          const index = message.messages.findIndex(m => m.id === enhanced.id);
          if (index !== -1) {
            message.messages[index] = enhanced;
            isUpdate = true;
          } else message.messages.push(enhanced);
          return { ...message, messages: [...message.messages] };
        });
        toast.success(`${isUpdate ? "Updated" : "Created"} the center message.`);
        return newMessages;
      });
      setIsEditing(false);
    }
  }, [upsertMessage]);
  // #endregion

  // #region Actions
  const toggleSelectedCenter = (center: Center) =>
    setSelectedMessage(oldSelectedMessage => {
      if (!oldSelectedMessage) return oldSelectedMessage;
      const index = oldSelectedMessage.centers.findIndex(c => c.id === center.id);
      if (index === -1) oldSelectedMessage.centers.push(center);
      else oldSelectedMessage.centers.splice(index, 1);
      return { ...oldSelectedMessage };
    });
  const debouncedUpdateSelectedMessageMessage = useCallback(
    debounce((message: string) => setSelectedMessage(m => (m ? { ...m, message } : m)), 500),
    []
  );
  const debouncedUpdateSelectedMessageTitle = useCallback(
    debounce((title: string) => setSelectedMessage(m => (m ? { ...m, title } : m)), 500),
    []
  );
  const onTitleChange = useCallback((value: string) => {
    setMessageTitle(value);
    debouncedUpdateSelectedMessageTitle(value);
  }, []);
  const onSelectCenter = (message: EnhancedCenterMessageCenter) => {
    setSelectedCmc(message);
    setSelectedMessage(undefined);
    setMessageMarkdown("");
  };
  const onMarkdownChange = useCallback((value: string) => {
    setMessageMarkdown(value);
    debouncedUpdateSelectedMessageMessage(value);
  }, []);
  const onMessageClick = useCallback((message: EnhancedCenterMessage) => {
    setSelectedMessage({ ...message });
    setMessageTitle(message.title);
    setMessageMarkdown(message.message);
  }, []);
  const onTimeSpanChange = (start?: DateTime, end?: DateTime) => {
    if (!start || !end || !start.isValid || !end.isValid) return;
    if (timeRange.start.toISO() === start.toISO() && timeRange.end.toISO() === end.toISO()) return;
    setTimeRange({ start, end });
    const nextDraft = { start, end };
    setTimeRangeDraft(nextDraft);
    timeRangeDraftRef.current = nextDraft;
    // if our preview date is already within the new range, do nothing
    if (previewDate >= start && previewDate <= end) return;
    // otherwise, update the preview to the range bound that is closest to today
  };

  const commitTimeSpanDraft = (start: DateTime | null, end: DateTime | null) =>
    onTimeSpanChange(start || undefined, end || undefined);

  const scheduleTimeSpanDraftCommit = (start: DateTime | null, end: DateTime | null) => {
    if (timeSpanCommitTimeoutRef.current) {
      window.clearTimeout(timeSpanCommitTimeoutRef.current);
    }

    timeSpanCommitTimeoutRef.current = window.setTimeout(() => {
      commitTimeSpanDraft(start, end);
      timeSpanCommitTimeoutRef.current = null;
    }, 500);
  };

  const flushTimeSpanDraftCommit = (start: DateTime | null, end: DateTime | null) => {
    if (timeSpanCommitTimeoutRef.current) {
      window.clearTimeout(timeSpanCommitTimeoutRef.current);
      timeSpanCommitTimeoutRef.current = null;
    }

    commitTimeSpanDraft(start, end);
  };

  const parseDateTimeInput = (value: string) => {
    const normalizedValue = value
      .replace(/[\u200e\u2066-\u2069]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    const formats = [
      "MM/dd/yyyy hh:mm a",
      "M/d/yyyy h:mm a",
      "MM/dd/yyyy h:mm a",
      "M/d/yyyy hh:mm a",
      "MM/dd/yyyy, hh:mm a",
      "M/d/yyyy, h:mm a"
    ];

    for (const format of formats) {
      const parsed = DateTime.fromFormat(normalizedValue, format, { locale: "en-US" });
      if (parsed.isValid) {
        return parsed;
      }
    }

    return null;
  };

  const onStartTimeRangeBlur = (inputValue: string) => {
    const parsed = parseDateTimeInput(inputValue);
    const start = parsed || timeRangeDraftRef.current.start;
    flushTimeSpanDraftCommit(start, timeRangeDraftRef.current.end);
  };

  const onEndTimeRangeBlur = (inputValue: string) => {
    const parsed = parseDateTimeInput(inputValue);
    const end = parsed || timeRangeDraftRef.current.end;
    flushTimeSpanDraftCommit(timeRangeDraftRef.current.start, end);
  };

  useEffect(
    () => () => {
      if (timeSpanCommitTimeoutRef.current) {
        window.clearTimeout(timeSpanCommitTimeoutRef.current);
      }
    },
    []
  );
  const onTimeSpanDraftChange = (start: DateTime | null, end: DateTime | null) => {
    const nextDraft = { start, end };
    setTimeRangeDraft(nextDraft);
    timeRangeDraftRef.current = nextDraft;
  };
  const onValidTimeChange = (start?: DateTime, end?: DateTime) => {
    if (!start) return;
    setSelectedMessage(oldMessage => {
      if (!oldMessage) return oldMessage;
      return {
        ...oldMessage,
        startDate: start,
        startDateUtc: start.isValid ? start.toUTC().toISO() || oldMessage.startDateUtc : oldMessage.startDateUtc,
        endDate: end,
        endDateUtc: end
          ? end.isValid
            ? end.toUTC().toISO() || oldMessage.endDateUtc
            : oldMessage.endDateUtc
          : undefined
      };
    });
  };
  const onNewMessage = () => {
    const startDate = DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    setSelectedCmc(undefined);
    setSelectedMessage({
      message: "",
      title: "",
      startDate,
      startDateUtc: startDate.toISO(),
      centers: selectedCmc?.center ? [selectedCmc.center] : []
    });
    setIsEditing(true);
  };
  const onCancel = () => {
    setMessageMarkdown("");
    setSelectedMessage(undefined);
    setIsEditing(false);
  };
  //#endregion

  return (
    <ToolWrapper
      title="Center Messages"
      readme={CenterMessagesReadme.tool}
      isLoading={getMessagesLoading || !dynamicContentView}
    >
      <Grid container wrap="nowrap" columnSpacing={4} sx={{ width: "100%", height: "100%", overflow: "hidden", p: 2 }}>
        <Grid item container xs={4} direction="column" wrap="nowrap" rowSpacing={2}>
          <Grid item>
            <AdminReadmeFormLabel
              label="Messages Active For Time Span"
              readme={CenterMessagesReadme.queryMessagesTimespan}
            />
          </Grid>
          <Grid item>
            <DateTimePicker
              label="Start Time"
              value={timeRangeDraft.start}
              onChange={newValue => onTimeSpanDraftChange(newValue, timeRangeDraft.end)}
              onAccept={newValue => scheduleTimeSpanDraftCommit(newValue, timeRangeDraftRef.current.end)}
              slotProps={{
                textField: {
                  onBlur: event => onStartTimeRangeBlur(event.target.value)
                }
              }}
            />
          </Grid>
          <Grid item>
            <DateTimePicker
              label="End Time"
              value={timeRangeDraft.end}
              onChange={newValue => onTimeSpanDraftChange(timeRangeDraft.start, newValue)}
              onAccept={newValue => scheduleTimeSpanDraftCommit(timeRangeDraftRef.current.start, newValue)}
              slotProps={{
                actionBar: { actions: ["clear"] },
                textField: {
                  onBlur: event => onEndTimeRangeBlur(event.target.value)
                }
              }}
            />
          </Grid>
          <Grid item xs style={{ overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Center</TableCell>
                  <TableCell align="right">
                    {isEditing ? `${selectedMessage?.centers.length} Selected` : "Messages (Live/All)"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {centerMessages.map(centerMessage => {
                  const liveMessages = EnhancedCenterMessage.filterMessages(centerMessage.messages, previewDate);
                  return (
                    <React.Fragment key={centerMessage.center.id}>
                      <TableRow
                        selected={
                          isEditing
                            ? selectedMessage?.centers.some(c => c.id === centerMessage.center.id)
                            : centerMessage.center.id === selectedCmc?.center.id
                        }
                        onClick={() =>
                          isEditing
                            ? !selectedMessage?.id && toggleSelectedCenter(centerMessage.center)
                            : onSelectCenter(centerMessage)
                        }
                      >
                        <TableCell>{centerMessage.center.name}</TableCell>
                        <TableCell align="right">
                          {!isEditing && `${liveMessages.length}/${centerMessage.messages.length}`}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse
                            in={!isEditing && centerMessage.center.id === selectedCmc?.center.id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box margin={1}>
                              {centerMessage.messages.length ? (
                                <Table size="small" padding="none">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Title</TableCell>
                                      <TableCell>Start Date</TableCell>
                                      <TableCell>End Date</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {centerMessage.messages.map((message, index) => (
                                      <TableRow
                                        hover
                                        onClick={() => onMessageClick(message)}
                                        selected={message.id === selectedMessage?.id}
                                        key={index}
                                      >
                                        <TableCell>{message.title}</TableCell>
                                        <TableCell>{message.startDate.toLocaleString(DateTime.DATETIME_MED)}</TableCell>
                                        <TableCell>
                                          {message.endDate?.toLocaleString(DateTime.DATETIME_MED) || ""}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography variant="body2" gutterBottom component="div">
                                  <strong>No Messages</strong>
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
        <Grid item container direction="column" justifyContent="space-between" wrap="nowrap" xs>
          <Grid item container direction="column" xs rowSpacing={1}>
            <Grid item>
              <AdminReadmeFormLabel label="Valid Time" readme={CenterMessagesReadme.validDuration} />
            </Grid>
            <Grid item>
              <DateTimePicker
                label="Start Time"
                value={selectedMessage?.startDate}
                onChange={newValue => newValue && onValidTimeChange(newValue, selectedMessage?.endDate)}
              />
            </Grid>
            <Grid item>
              <DateTimePicker
                label="End Time"
                value={selectedMessage?.endDate}
                onChange={newValue => onValidTimeChange(selectedMessage?.startDate, newValue || undefined)}
                slotProps={{ actionBar: { actions: ["clear"] } }}
              />
            </Grid>
            <Grid item>
              <AdminReadmeFormLabel label="Title" readme={CenterMessagesReadme.title} />
            </Grid>
            <Grid item>
              <TextField
                type="text"
                disabled={!isEditing}
                value={messageTitle}
                onChange={event => onTitleChange(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item>
              <AdminReadmeFormLabel label="Message" readme={CenterMessagesReadme.message} />
            </Grid>
            <Grid item xs style={{ overflow: "auto" }}>
              <TextField
                type="text"
                minRows={6}
                value={messageMarkdown}
                onChange={event => isEditing && onMarkdownChange(event.target.value)}
                multiline
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid item container justifyContent="space-around">
            {isEditing ? (
              <>
                <Grid item>
                  <Button onClick={onCancel} disabled={upsertMessageLoading} color="secondary">
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  {upsertMessageLoading ? (
                    <CircularProgress size="2rem" />
                  ) : (
                    <Button
                      disabled={!selectedMessage?.centers.length || !selectedMessage.message}
                      onClick={() => selectedMessage && setUpdateMessage({ ...selectedMessage })}
                    >
                      Save Message
                    </Button>
                  )}
                </Grid>
              </>
            ) : (
              <>
                <Grid item>
                  <Button
                    onClick={() => selectedMessage && setIsEditing(s => !s)}
                    disabled={!selectedMessage || (selectedMessage.endDate && selectedMessage.endDate < DateTime.now())}
                  >
                    Edit Message
                  </Button>
                </Grid>
                <Grid item>
                  <Button onClick={onNewMessage}>New Message</Button>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        <Grid item container xs={4} direction="column" wrap="nowrap" rowSpacing={2}>
          <Grid item>
            <AdminReadmeFormLabel label="Preview" readme={CenterMessagesReadme.preview} />
          </Grid>
          <Grid item>
            <DateTimePicker
              label="Preview Time"
              value={previewDate}
              onChange={newValue => newValue && setPreviewDate(newValue)}
              slotProps={{ actionBar: { actions: ["clear"] } }}
            />
          </Grid>
          <Grid item xs style={{ overflow: "auto" }}>
            {dynamicContentView && (
              <DynamicContentPanel
                dynamicContentView={dynamicContentView}
                setDynamicContentView={setDynamicContentView}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </ToolWrapper>
  );
};

export default CenterMessages;
